import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { APP_LANDINGI_URL, MICROSOFT_CLIENT_ID } from '@config/env'
import * as Sentry from '@sentry/react'
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

export type MicrosoftProviderProps = {
  children: ReactNode
}

export type MicrosoftProviderValue = {
  isMicrosoftButton: boolean
}

type MsalInstanceType = PublicClientApplication | null

type MsalProviderWrapperProps = {
  children: ReactNode
  msalInstance: MsalInstanceType
}

const MicrosoftContext = createContext<MicrosoftProviderValue | null>(null)

const msalConfig = {
  auth: {
    clientId: MICROSOFT_CLIENT_ID,
    authority: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    redirectUri: `${APP_LANDINGI_URL}/api/auth/oauth-popup-callback`
  },
  cache: {
    storeAuthStateInCookie: true
  }
}

const MsalProviderWrapper: FC<MsalProviderWrapperProps> = ({
  children,
  msalInstance
}) => {
  if (!msalInstance) {
    return children
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>
}

export const MicrosoftProvider: FC<MicrosoftProviderProps> = ({ children }) => {
  const [msalInstance, setMsalInstance] = useState<MsalInstanceType>(null)

  useEffect(() => {
    import('@azure/msal-browser')
      .then(({ PublicClientApplication }) => {
        const instance = new PublicClientApplication(msalConfig)

        setMsalInstance(instance)
      })
      .catch(error => {
        Sentry.withScope(scope => {
          scope.setExtra('microsoft-error', true)
          scope.setExtra('msalConfig', msalConfig)
          scope.setLevel('error')
          Sentry.captureException(error)
          Sentry.captureMessage('MSAL initialization failed')
        })
      })
  }, [])

  const ctxValue = useMemo(
    () => ({
      isMicrosoftButton: !!msalInstance
    }),
    [msalInstance]
  )

  return (
    <MicrosoftContext.Provider value={ctxValue}>
      <MsalProviderWrapper msalInstance={msalInstance}>
        {children}
      </MsalProviderWrapper>
    </MicrosoftContext.Provider>
  )
}

export const useMicrosoftContext = () => {
  const ctxValue = useContext(MicrosoftContext)

  if (!ctxValue) {
    throw new Error('useMicrosoftContext must be used inside MicrosoftProvider')
  }

  return ctxValue
}
